/**
 * Procedural squishy tunables (design doc §19). Shapes and parts are data in
 * `@heartpatch/shared` (`BODIES`, `PARTS`); this file holds the look and
 * motion numbers shared by every squishy. All first guesses to judge in the
 * dev gallery (`/gallery.html`) and on the playtest devices.
 */

import { ART_RULES } from '@heartpatch/shared';

/**
 * Fixed toy colours (sRGB hex) for the `ink`, `white` and `blush` palette
 * roles. A species may set its own ink (`visual.ink`, ART_BIBLE §1.7).
 */
export const FIXED_COLORS = {
  ink: ART_RULES.defaultInk,
  white: '#ffffff',
  blush: '#ff9db5', // TUNE
} as const;

/** Per-squishy variation, seeded from the instance id. */
export const VARIATION = {
  /** Overall lightness change, as a fraction (±); face contrast is checked at both ends. */
  lightness: ART_RULES.lightnessVariation,
  /** Warm/cool shift moved between red and blue, 0–1 sRGB (±); face contrast is checked at both ends. */
  warmth: ART_RULES.warmthVariation,
  /** Placement wobble for single and paired parts, in degrees (±). */
  placementDeg: 2.5, // TUNE
  /** Breaths per second, picked in [min, max]. */
  breathRate: { min: 0.32, max: 0.45 }, // TUNE
  /** Breathing squash as a fraction of height, picked in [min, max]. */
  breathAmplitude: { min: 0.022, max: 0.032 }, // TUNE
  /** Scattered pattern pieces stay at least this far apart, in degrees. */
  scatterSpacingDeg: 18, // TUNE
  /** Placement tries per scattered piece before it's left out. */
  scatterTries: 8,
} as const;

/**
 * Detail levels. `low` is the map view and the low quality tier; `high` is
 * close-ups (wardrobe, care, battle) on medium and high tiers. Both are
 * smooth-shaded, so low only loses silhouette roundness up close.
 */
export type SquishyLod = 'low' | 'high';

/**
 * Detail a squishy field can draw at: the shared levels, plus `hero` for the
 * one squishy that fills the screen in the close-up view (#20). Keepers stay
 * on `SquishyLod`.
 */
export type SquishyDetail = SquishyLod | 'hero';

export interface LodSettings {
  /** Body rings top to bottom; the body has twice as many segments around. */
  bodyRings: number;
  /** Rings on a part primitive. */
  partRings: number;
  /** Segments around a part primitive. */
  partSegments: number;
}

export const LOD: Readonly<Record<SquishyDetail, LodSettings>> = {
  low: { bodyRings: 14, partRings: 6, partSegments: 10 }, // TUNE
  high: { bodyRings: 32, partRings: 12, partSegments: 20 }, // TUNE
  // TUNE: one squishy at full-screen size on a 2× display, so the silhouette
  // stays round (~9k body triangles; still one draw call per shape).
  hero: { bodyRings: 48, partRings: 16, partSegments: 28 },
};

/**
 * A heart's (`cleft`) outline turns sharply at its shoulders and cleft, so
 * its body gets this many times the rings (and segments around) of other
 * bodies at every detail level. About 2.3× its body's triangles: a
 * heart-bodied squishy is ~16k triangles at `high`, inside ART_BIBLE's budget.
 */
export const CLEFT_RING_SCALE = 1.5; // TUNE

/** Soft-vinyl material (matches the test scene's vinyl). */
export const VINYL = {
  roughness: 0.42, // TUNE
  clearCoatIntensity: 0.9, // TUNE
  clearCoatRoughness: 0.2, // TUNE
  /** Rim light (linear RGB) so squishies pop off the ground. */
  rimColor: [1, 0.93, 0.97], // TUNE
  rimStrength: 0.35, // TUNE
} as const;

/**
 * How a squishy is drawn: `normal`, or `shadow` for the Hollow's rescue
 * guardians (owner decision 7, 2026-10-03): dark lavender, a soft glowing rim
 * and glassy eyes, on the same meshes in the same draw calls.
 */
export type SquishyLook = 'normal' | 'shadow';

/**
 * Look codes in the `squishMotion` attribute's spare `w`: 0 normal, then the
 * shadow look for the body and parts, and for the eyes (and their glints).
 */
export const SQUISH_LOOK_CODE = { normal: 0, shadow: 1, shadowEyes: 2 } as const;

/** The shadow look (linear RGB). Judge on the battle arena and in the gallery (`?shadow`). */
export const SHADOW_LOOK = {
  /** Dark lavender the vinyl is tinted towards, keeping its shading. */
  tint: [0.11, 0.07, 0.22], // TUNE
  /** How far (0–1) the colour moves towards the tint. */
  tintMix: 0.9, // TUNE
  /** Eyes keep their own colour, mixed this far towards the glow: soft, glassy, readable. */
  eyeGlowMix: 0.55, // TUNE
  /** The soft glowing rim: colour, strength and falloff (higher hugs the edge). */
  glow: [0.72, 0.55, 1], // TUNE
  glowStrength: 0.75, // TUNE
  glowFalloff: 3.5, // TUNE
} as const;

/**
 * Rarity material tiers and glow (ART_BIBLE §1.4), drawn by the squish
 * shader from a per-instance code: no extra meshes or draw calls.
 */
export const FINISH = {
  /** Sparkle flecks: cells per body height, share of cells with a fleck, fleck strength. */
  sparkle: {
    cells: 11, // TUNE
    density: 0.16, // TUNE
    strength: 0.9, // TUNE
    /**
     * For a species with `visual.sparkleOffFace`: no flecks where its body
     * faces forward more than this (the face, −z) or down more than `under`
     * (a heart's bottom point), as the mesh normal's z and y.
     */
    offFace: { front: 0.5, under: 0.55 }, // TUNE
  },
  /** Iridescent rim: strength and how tightly it hugs the edge. */
  iridescent: { strength: 0.55, falloff: 2.2 }, // TUNE
  /**
   * Mythic shimmer (#261): iridescent plus pearly bands that glide over the
   * vinyl as the view turns. Band count across a body height, how much of
   * each band is lit, its strength, and the pearl tint (linear RGB).
   */
  shimmer: { bands: 3.2, width: 0.12, strength: 0.26, tint: [1, 0.9, 0.98] }, // TUNE
  /**
   * The softer shimmer Mythic costumes wear (owner, #261): fewer, wider bands
   * at lower strength, so one pearly sweep crosses a dark, narrow figure
   * instead of thin stripes. Squishies keep `shimmer` above.
   */
  shimmerSoft: { bands: 1.3, width: 0.3, strength: 0.14, tint: [1, 0.9, 0.98] }, // TUNE
  /** Glow: how much of the vinyl's own colour is added back as light. */
  glow: 0.28, // TUNE
} as const;

/**
 * Codes in `squishEvent`'s spare `w`: the finish, plus `glow` when the instance
 * glows, plus `offFace` on the body of a species with `visual.sparkleOffFace`.
 * Each sits above everything before it, so each sum decodes to one set.
 */
export const FINISH_CODE = {
  vinyl: 0,
  sparkle: 1,
  iridescent: 2,
  shimmer: 3,
  shimmerSoft: 4,
  glow: 8,
  /** Above glow: a body that keeps its sparkle flecks off its face and underside. */
  offFace: 16,
} as const;

/**
 * Rare finds glow (#391, the owner picked the coloured edge). Rare and epic
 * squishies get an outline that glows in each part's own colour; legendary
 * and up already wear a rainbow edge (iridescent, shimmer), so theirs glows
 * brighter instead. Faces and patterns never glow. The level per rarity is
 * shared data (`RARE_GLOW_LEVEL`). Judge in the gallery and roster sheets.
 */
export const RARE_GLOW = {
  /**
   * Edge glow strength for rare (level 1) and epic (level 2). Legendary and up
   * always wear a rainbow edge (`finishByRarity`), so they use `rainbow` instead.
   */
  edge: [0.42, 0.52], // TUNE
  /** How tightly the edge glow hugs the outline (higher is thinner). */
  falloff: 1.7, // TUNE
  /** Below this much edge glow a pixel skips the glow's colour work (it would add under 1/255). */
  minEdge: 0.004, // TUNE
  /** How much richer than the part's own colour the glow is (pushed away from grey, same hue). */
  saturate: 1.25, // TUNE
  /** A pale part's glow is pushed to at least this saturation, keeping its own hue. */
  minSaturation: 0.55, // TUNE
  /** A part's glow brightness is its own brightness times this (capped at 1, floored), so brown glows brown. */
  darkLift: 1.6, // TUNE
  darkFloor: 0.12, // TUNE
  /** How much less glow near-white, pale vinyl takes, so it doesn't clip to plain white. */
  paleCalm: 0.3, // TUNE
  /** Saturation of the palette colour a pure white part borrows. */
  paletteSaturation: 0.7, // TUNE
  /** How much brighter the rainbow edge glows at level 3 (legendary, secret) and 4 (mythic). */
  rainbow: [2.6, 3.2], // TUNE
} as const;

/**
 * Where the rare glow rides in `squishEvent.w`, above every `FINISH_CODE`
 * sum (which stays below `level`): the level times `level`, plus the glow
 * hue (one of `hues`, from the palette) times `hue`. Floats hold these sums
 * exactly.
 */
export const RARE_GLOW_CODE = { level: 32, hue: 160, hues: 36 } as const;

/** A material finish a piece can be drawn with: the shared `Finish`s plus the costumes' soft shimmer. */
export type FinishName = Exclude<keyof typeof FINISH_CODE, 'glow' | 'offFace'>;

/** Event animations. Each shader formula decays to rest within its duration. */
export type SquishMove = 'jiggle' | 'wobble' | 'bounce';

/** Kind codes in the `squishEvent` attribute; 0 means none. */
export const SQUISH_MOVE_CODE: Readonly<Record<SquishMove, number>> = {
  jiggle: 1,
  wobble: 2,
  bounce: 3,
};

export const SQUISH = {
  /** Seconds each move runs before the squishy is at rest again. */
  duration: { jiggle: 0.9, wobble: 1, bounce: 1.1 }, // TUNE
  /** Tap jiggle: squash amplitude, side lean, decay per second, wobble speed (rad/s). */
  jiggle: { squash: 0.16, lean: 0.1, decay: 6, speed: 30 }, // TUNE
  /** Landing wobble: squash amplitude, decay per second, speed (rad/s). */
  wobble: { squash: 0.28, decay: 5, speed: 17 }, // TUNE
  /** Happy bounce: hop height (fraction of body height), hops, squash on landing. */
  bounce: { height: 0.45, hops: 2, squash: 0.22 }, // TUNE
} as const;

/** Batch keys (and so mesh names, after `squishy-`) of floater glyphs start with this. */
export const FLOATER_BATCH = 'floater:';

/**
 * Floaters (a species' `visual.floater`): a flat, camera-facing glyph beside
 * the squishy. Where it floats is species data; this is how every glyph is
 * drawn: a chunky shape over a slightly bigger outline of itself.
 */
export const FLOATER = {
  /** Outline width around the glyph, a share of the glyph's height. */
  outline: 0.055, // TUNE
  /** The outline's colour: the glyph's own colour times this (linear RGB), a deep shade of it. */
  outlineShade: [0.13, 0.08, 0.15], // TUNE
  /** How far the glyph sits in front of its outline, a share of its height (no z-fighting). */
  layerGap: 0.01, // TUNE
  /** How far the glyph's shading leans out at its edges (0 flat): a soft dome that catches the gloss. */
  dome: 0.7, // TUNE
} as const;

/**
 * A glum face (#393, a squishy not keen on its job), made from its own face
 * parts: the eyes half close (their height times `eyeLid`) and the mouth turns
 * upside down, so a smile becomes a little frown. No new parts or meshes.
 */
export const GLUM_FACE = { eyeLid: 0.55 } as const; // TUNE

/** Contact shadow: a soft dark disc under each squishy. */
export const CONTACT_SHADOW = {
  color: '#6b4b6e', // TUNE: plum, matches the test scene
  /** Diameter as a multiple of the body's larger footprint side. */
  scale: 1.35, // TUNE
} as const;
